import React, { useState, useEffect, useMemo, useContext } from "react";
import { PermissionsContext } from "../../utils/PermissionsContext";
import {
  Box,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Button,
  TextField,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  InputLabel,
  FormControl,
  useMediaQuery,
  Collapse,
  Divider,
  Tooltip,
  InputAdornment,
  IconButton,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import EmailIcon from "@mui/icons-material/Email";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SecurityIcon from "@mui/icons-material/Security";
import FlagIcon from "@mui/icons-material/Flag";
import BuildIcon from "@mui/icons-material/Build";
import FolderIcon from "@mui/icons-material/Folder";
import SendIcon from "@mui/icons-material/Send";
import SaveIcon from "@mui/icons-material/Save";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import ExtensionIcon from "@mui/icons-material/Extension";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import KeyIcon from "@mui/icons-material/Key";
import AIChatSettings from "./AIChatSettings";
import ApiKeysSettings from "./ApiKeysSettings";

import OwnerDashboardSettings from "./OwnerDashboardSettings";
import HOADocuments from "./HOADocuments";
import Statuses from "./Statuses";
import Company from "./Company";
import MemberDataSourceCard from "./MemberDataSourceCard";
import Uploads from "./Uploads";
import Roles from "./Roles";
import WorkOrderManager from "./WorkOrderManager";
import ArchitecturalRequestManager from "./ArchitecturalRequestManager";
import ViolationstManager from "./ViolationManager";
import NotificationLetter from "./Coverletters";
import PriorityLevels from "./PriorityLevels";
import Notifications from "./EmailNotifications";
import EmailTemplate from "./EmailTemplate";
import IntegrationSettings from "./IntegrationSettings";
import AuditLogs from "./AuditLogs";
import EmailDeliveryLog from "./EmailDeliveryLog";
import MarinaSettings from "./MarinaSettings";
import ModuleSettings from "./ModuleSettings";
import SSOSettings from "./SSOSettings";
import WorkOrderBillingConfig from "./WorkOrderBillingConfig";
import api from "../../utils/api";

/* ── Per-section help content ───────────────────────────────────────────── */
const SECTION_HELP = {
  0: {
    title: "General Settings",
    tips: [
      "This is your HOA's profile. name, address, phone, and contact details.",
      "This information appears on violation letters, work order notifications, cover letters, and the owner portal.",
      "Keep it up to date so members always see accurate contact information.",
    ],
  },
  1: {
    title: "Notifications",
    tips: [
      "Configure which system events trigger automatic email alerts (e.g. new work order, violation issued).",
      "You can specify different recipients for each event type.",
      "Changes take effect immediately. no restart needed.",
    ],
  },
  2: {
    title: "Email Configuration",
    tips: [
      "Set up the outgoing mail server (SMTP) the system uses to send all automated emails.",
      "Common ports: 587 (TLS/STARTTLS. recommended), 465 (SSL), 25 (legacy, often blocked).",
      "Enable SMTP Authentication if your mail provider requires a username and password.",
      'Use "Send Test Email" to verify your settings before saving. a test message will be sent to the address in the Test Recipient field.',
    ],
  },
  3: {
    title: "Uploads",
    tips: [
      "Manage files and documents that have been uploaded into the system.",
      "Files uploaded here are available for use in letters, templates, and the owner portal.",
      "Check your plan's storage limits if you encounter upload errors.",
    ],
  },
  4: {
    title: "Statuses",
    tips: [
      "Statuses define the lifecycle of work orders, violations, and architectural requests.",
      "Each module has its own independent set of statuses.",
      'Records in a "Closed" or terminal status are locked from further edits.',
      "Reordering statuses here affects the order they appear in dropdowns throughout the app.",
    ],
  },
  5: {
    title: "Roles",
    tips: [
      "Roles control what each user type can see and do across the entire portal.",
      "Changes to a role apply instantly to every user currently assigned that role.",
      "Be careful when removing permissions. users will lose access to those features immediately.",
      "The Member role (ID 3) controls the owner portal experience.",
    ],
  },
  6: {
    title: "Priority Levels",
    tips: [
      "Priority levels are urgency labels (e.g. Low, Medium, High, Urgent) used when creating violations and work orders.",
      "You can add custom levels and assign a color to each for easy visual scanning.",
      "Levels are shared across violations and work orders.",
    ],
  },
  7: {
    title: "Email Templates",
    tips: [
      "Customise the body of every automated email the system sends.",
      "Use the variable tags shown in the editor (e.g. {{member_name}}, {{property_address}}) to insert dynamic values.",
      "Changes are applied to the next email sent. existing sent emails are not affected.",
      "Always send a test email after editing a template to check formatting.",
    ],
  },
  8: {
    title: "Integration Settings",
    tips: [
      "Connect the portal to third-party services such as Jonas Software for billing and account sync.",
      "Credentials entered here are encrypted at rest and never shown in plain text after saving.",
      "If a sync fails, check the integration log for error details before re-entering credentials.",
    ],
  },
  9: {
    title: "HOA Documents",
    tips: [
      "Upload community documents (bylaws, rules, forms, meeting minutes) that members can access.",
      "Documents published here appear in the owner portal under the Documents section.",
      "Supported formats: PDF, Word (.docx), Excel (.xlsx), and common image types.",
    ],
  },
  10: {
    title: "Notification Letter",
    tips: [
      "Configure the cover letter template that wraps violation and work order notifications sent to members.",
      "Use the variable tags to personalise each letter with the member's name, property address, and issue details.",
      "Preview your changes using the Preview button before saving.",
    ],
  },
  11: {
    title: "Owner Dashboard Settings",
    tips: [
      "Control which modules and information are visible to members on the owner portal.",
      "Disabling a module hides it from all members immediately.",
      "These settings do not affect admin or staff users. only the member-facing portal.",
    ],
  },
  100: {
    title: "Work Order Manager",
    tips: [
      "Define the types and categories of work orders (e.g. Plumbing, Electrical, Landscaping).",
      "These types appear in the dropdown when staff create a new work order.",
      "You can also set default priority levels and estimated resolution times per type.",
    ],
  },
  101: {
    title: "Architectural Request Manager",
    tips: [
      "Define the types of architectural change requests members can submit (e.g. Fence, Deck, Addition, Pool).",
      "Each type can have its own required documents and review checklist.",
      "Types added here appear immediately in the member portal submission form.",
    ],
  },
  102: {
    title: "Violations Manager",
    tips: [
      "Define violation categories and their default fine amounts.",
      "These categories are available when staff issue a new violation.",
      "Fine amounts set here are defaults. staff can override them per violation if permitted.",
    ],
  },
  12: {
    title: "Audit Logs",
    tips: [
      "Every action taken in the portal is recorded here. who did it, when, and from which module.",
      "Use the filters to narrow down by module, action type, user, or date range.",
      "Click any row to expand the full detail payload for that log entry.",
      "Use Export CSV to download the current filtered view for compliance or reporting.",
    ],
  },
  13: {
    title: "Marina Settings",
    tips: [
      "Add and manage marinas here. name, address, GPS coordinates, and active status.",
      "Once a marina is created, go to the Marina module to configure its docks, slips, reservations, and map layout.",
      "Deleting a marina is permanent and removes all associated docks, slips, and reservations.",
    ],
  },
  14: {
    title: "Module Settings",
    tips: [
      "Enable or disable entire system modules from this page.",
      "Deactivating a module hides it from the sidebar and blocks direct URL access for all users immediately.",
      "Module status is independent of user permissions. a module must be both active and permitted to appear.",
      "At least one module must remain active at all times.",
    ],
  },
  15: {
    title: "SSO / Single Sign-On",
    tips: [
      "Allow users to sign in with Google or a SAML 2.0 identity provider (Okta, Azure AD, OneLogin, etc.).",
      "Google Sign-In requires creating an OAuth 2.0 credential in the Google Cloud Console.",
      "SAML SSO requires configuring this portal as a service provider in your identity provider.",
      "Both providers can be enabled simultaneously. users will see the relevant buttons on the login page.",
    ],
  },
  16: {
    title: "Billing Triggers",
    tips: [
      "Configure which work order statuses automatically mark a record as billable.",
      "When a work order transitions into a trigger status, the billing flag is set without manual intervention.",
      "Changes take effect immediately for any work orders that move to the configured statuses after saving.",
    ],
  },
};

/* ── Sidebar nav descriptions (shown as hover tooltips) ─────────────────── */
const TAB_DESCRIPTIONS = {
  0:   "HOA name, address, and contact information",
  1:   "Configure automatic email alerts for system events",
  2:   "SMTP server, sender address, and test email delivery",
  3:   "Manage uploaded files and documents",
  4:   "Work order, violation, and request lifecycle stages",
  5:   "User role permissions across the portal",
  6:   "Urgency labels used on violations and work orders",
  7:   "Customise automated email body content",
  8:   "Third-party service connections (e.g. Jonas)",
  9:   "Community documents available to members",
  10:  "Cover letter template for member notifications",
  11:  "Control what members see on the owner portal",
  100: "Work order types and categories",
  101: "Architectural request submission types",
  102: "Violation categories and default fines",
  12:  "Full activity history. who did what and when",
  13:  "Create and manage marina locations",
  14:  "Enable or disable system modules app-wide",
  15:  "Google and SAML single sign-on configuration",
  16:  "Work order statuses that automatically trigger billing",
};

/* ── SectionHelp banner ─────────────────────────────────────────────────── */
const SectionHelp = ({ tabIndex, open, onToggle }) => {
  const content = SECTION_HELP[tabIndex];
  if (!content) return null;

  return (
    <Box
      sx={{
        mb: 3,
        borderRadius: "14px",
        border: "1px solid rgba(47,99,173,0.18)",
        backgroundColor: "rgba(47,99,173,0.04)",
        overflow: "hidden",
      }}
    >
      {/* Header row */}
      <Box
        onClick={onToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2,
          py: 1.25,
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { backgroundColor: "rgba(47,99,173,0.06)" },
        }}
      >
        <LightbulbOutlinedIcon sx={{ color: "#2f63ad", fontSize: 20 }} />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: "#1e3a5f", flex: 1 }}
        >
          How this section works
        </Typography>
        {open ? (
          <KeyboardArrowUpIcon sx={{ color: "#5f6b7a", fontSize: 20 }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ color: "#5f6b7a", fontSize: 20 }} />
        )}
      </Box>

      {/* Collapsible tips */}
      <Collapse in={open}>
        <Box
          sx={{
            px: 2,
            pb: 1.75,
            pt: 0.25,
            borderTop: "1px solid rgba(47,99,173,0.10)",
          }}
        >
          <Box
            component="ul"
            sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.6 }}
          >
            {content.tips.map((tip, i) => (
              <Typography
                key={i}
                component="li"
                variant="body2"
                sx={{ color: "#3a4a5c", lineHeight: 1.55 }}
              >
                {tip}
              </Typography>
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

/* ── FieldHint. small info icon with tooltip ───────────────────────────── */
const FieldHint = ({ text }) => (
  <Tooltip title={text} placement="top" arrow>
    <InfoOutlinedIcon
      sx={{
        fontSize: 17,
        color: "rgba(47,99,173,0.55)",
        cursor: "help",
        "&:hover": { color: "#2f63ad" },
      }}
    />
  </Tooltip>
);

// Permission required to see each tab (null = any authenticated admin can see it)
const TAB_PERMISSIONS = {
  0:   "view_system_general",
  1:   "view_system_notifications",
  2:   "view_system_email_config",
  3:   "view_system_uploads",
  4:   "view_system_statuses",
  5:   "view_system_roles",
  6:   "view_system_priority_levels",
  7:   "view_system_email_templates",
  8:   "view_system_integration",
  9:   "view_system_hoa_documents",
  10:  "view_system_notification_letter",
  11:  "view_system_owner_dashboard",
  12:  "view_system_audit_logs",
  13:  "view_system_marina_settings",
  14:  "view_system_module_settings",
  15:  "view_system_sso",
  16:  "view_system_ai_chat",
  17:  "manage_system_admin_roles", // API Keys. System Admin only
  100: "view_system_types_manager",
  101: "view_system_types_manager",
  102: "view_system_types_manager",
};

// All tabs. each belongs to a group
const ALL_TABS = [
  { id: 0,   label: "General",                  icon: <SettingsIcon />,                  group: "system" },
  { id: 14,  label: "Module Settings",          icon: <ExtensionIcon />,                 group: "system" },
  { id: 5,   label: "Roles",                    icon: <SecurityIcon />,                  group: "system" },
  { id: 15,  label: "SSO / Single Sign-On",     icon: <VpnKeyIcon />,                    group: "system" },
  { id: 12,  label: "Audit Logs",               icon: <ManageSearchIcon />,              group: "system" },
  { id: 20,  label: "Email Delivery Log",       icon: <EmailIcon />,                     group: "system" },

  { id: 2,   label: "Email Configuration",      icon: <EmailIcon />,                     group: "communications" },
  { id: 7,   label: "Email Templates",          icon: <MoveToInboxIcon />,               group: "communications" },
  { id: 1,   label: "Notifications",            icon: <NotificationsActiveIcon />,       group: "communications" },
  { id: 10,  label: "Notification Letter",      icon: <FolderIcon />,                    group: "communications" },

  { id: 6,   label: "Priority Levels",          icon: <FlagIcon />,                      group: "operations" },
  { id: 11,  label: "Owner Dashboard",          icon: <DashboardIcon />,                 group: "operations" },

  { id: 100, label: "Work Order Manager",       icon: <BuildIcon />,                     group: "managers" },
  { id: 101, label: "Architectural Manager",    icon: <BuildIcon />,                     group: "managers" },
  { id: 102, label: "Violations Manager",       icon: <BuildIcon />,                     group: "managers" },

  { id: 13,  label: "Marina Settings",          icon: <DirectionsBoatIcon />,            group: "modules" },
  { id: 8,   label: "Integration Settings",     icon: <IntegrationInstructionsIcon />,   group: "modules" },
  { id: 16,  label: "AI Chat",                  icon: <SmartToyIcon />,                  group: "modules" },
  { id: 17,  label: "API Keys",                 icon: <KeyIcon />,                       group: "modules" },

  { id: 9,   label: "HOA Documents",            icon: <FolderIcon />,                    group: "content" },
  { id: 3,   label: "Uploads",                  icon: <CloudUploadIcon />,               group: "content" },
];

// Group definitions. order controls sidebar display order
const GROUPS = [
  { key: "system",         label: "System",               icon: <SettingsIcon sx={{ fontSize: 15 }} /> },
  { key: "communications", label: "Communications",        icon: <EmailIcon    sx={{ fontSize: 15 }} /> },
  { key: "operations",     label: "Operations",           icon: <ListAltIcon  sx={{ fontSize: 15 }} /> },
  { key: "managers",       label: "Types & Managers",     icon: <BuildIcon    sx={{ fontSize: 15 }} /> },
  { key: "modules",        label: "Modules & Integrations", icon: <ExtensionIcon sx={{ fontSize: 15 }} /> },
  { key: "content",        label: "Content & Media",      icon: <FolderIcon   sx={{ fontSize: 15 }} /> },
];

const SystemAdministration = () => {
  const permissions = useContext(PermissionsContext);
  const isSystemAdmin = permissions.includes("manage_system_admin_roles") ||
                        permissions.includes("view_system_ai_chat");
  const hasPermission = (perm) => !perm || permissions.includes(perm);

  const isMobile = useMediaQuery("(max-width:768px)");
  const [tabIndex, setTabIndex] = useState(
    () => parseInt(localStorage.getItem("adminTabIndex"), 10) || 0
  );
  // All groups open by default; persisted in localStorage
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = localStorage.getItem("adminOpenGroups");
      return saved ? JSON.parse(saved) : { system:true, communications:true, operations:true, managers:true, modules:true, content:true };
    } catch { return { system:true, communications:true, operations:true, managers:true, modules:true, content:true }; }
  });
  const toggleGroup = (key) => setOpenGroups(prev => {
    const next = { ...prev, [key]: !prev[key] };
    localStorage.setItem("adminOpenGroups", JSON.stringify(next));
    return next;
  });
  const [helpOpen, setHelpOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [testResult, setTestResult] = useState("");

  const [settings, setSettings] = useState({
    smtpServer: "",
    smtpPort: "587",
    smtpUsername: "",
    smtpPassword: "",
    smtpAuth: true,
    smtpSSL: true,
    fromEmail: "",
    emailFormat: "PDF",
    testRecipient: "",
  });

  // Build visible tabs per group (filtered by permission)
  const visibleGroups = useMemo(() =>
    GROUPS.map(group => ({
      ...group,
      tabs: ALL_TABS.filter(t => t.group === group.key && hasPermission(TAB_PERMISSIONS[t.id])),
    })).filter(g => g.tabs.length > 0),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [permissions]);

  useEffect(() => {
    localStorage.setItem("adminTabIndex", String(tabIndex));
    setHelpOpen(false); // reset help accordion on section change
  }, [tabIndex]);

  useEffect(() => {
    api
      .get(`/smtp-settings`)
      .then((res) => {
        if (res.data) {
          setSettings({
            smtpServer: res.data.smtp_server || "",
            smtpPort: res.data.smtp_port || "587",
            smtpUsername: res.data.smtp_username || "",
            smtpPassword: res.data.smtp_password || "",
            smtpAuth: res.data.smtp_auth ?? true,
            smtpSSL: res.data.smtp_ssl ?? false,
            fromEmail: res.data.from_email || "",
            emailFormat: res.data.email_format || "PDF",
            testRecipient: res.data.test_recipient || "",
          });
        }
      })
      .catch(() => setSaveError(true));
  }, []);

  const handleChange = (e) =>
    setSettings({ ...settings, [e.target.name]: e.target.value });

  const handleToggle = (e) =>
    setSettings({ ...settings, [e.target.name]: e.target.checked });

  const handleSave = async () => {
    try {
      await api.post(`/smtp-settings`, settings);
      setSaveSuccess(true);
    } catch {
      setSaveError(true);
    }
  };

  const testSMTPConnection = async () => {
    setLoading(true);
    setTestResult("");
    try {
      const response = await api.post(`/test-smtp`, {
        ...settings,
        toEmail: settings.testRecipient,
      });
      setTestResult(`✅ ${response.data.message}`);
    } catch (error) {
      setTestResult(
        `❌ SMTP Test Failed: ${error.response?.data?.message || error.message}`
      );
    }
    setLoading(false);
  };

  const fieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    borderColor: "rgba(47,99,173,0.25)",
    color: "#2f63ad",
    px: 2.25,
  };

  const settingsSectionSx = {
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
    backgroundColor: "rgba(255,255,255,0.96)",
    p: { xs: 2, md: 2.5 },
  };

  const switchRowSx = {
    mt: 1.5,
    px: 1.25,
    py: 1.1,
    borderRadius: "12px",
    backgroundColor: "rgba(47,99,173,0.04)",
    border: "1px solid rgba(47,99,173,0.08)",
  };

  const renderEmailConfiguration = () => (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
        >
          Email Configuration
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          Configure SMTP delivery, sender settings, and send a test email.
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gap: 2.5 }}>
        <Paper elevation={0} sx={settingsSectionSx}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
          >
            SMTP Settings
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
            Enter your outgoing mail server details.
          </Typography>

          <TextField
            fullWidth
            label="SMTP Server"
            name="smtpServer"
            value={settings.smtpServer}
            onChange={handleChange}
            placeholder="e.g. smtp.gmail.com or smtp.office365.com"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FieldHint text="Hostname of your outgoing mail server. Common values: smtp.gmail.com, smtp.office365.com, mail.yourdomain.com." />
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />

          <TextField
            fullWidth
            label="SMTP Port"
            name="smtpPort"
            value={settings.smtpPort}
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FieldHint text="587 = TLS/STARTTLS (recommended). 465 = SSL/TLS. 25 = legacy, often blocked by ISPs." />
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />

          <TextField
            fullWidth
            label="User Name"
            name="smtpUsername"
            value={settings.smtpUsername}
            onChange={handleChange}
            placeholder="e.g. noreply@yourhoa.com"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FieldHint text="The email address or username used to authenticate with your SMTP server. Usually the same as your 'From' address." />
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            name="smtpPassword"
            value={settings.smtpPassword}
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FieldHint text="For Gmail/Google Workspace, use an App Password rather than your regular account password. Go to Google Account → Security → App Passwords to generate one." />
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />

          <Box sx={switchRowSx}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.smtpAuth}
                  onChange={handleToggle}
                  name="smtpAuth"
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  SMTP server requires authentication
                  <FieldHint text="Almost all modern mail servers require authentication. Only disable this if your server explicitly allows unauthenticated relay (rare. typically internal mail relays)." />
                </Box>
              }
              sx={{ m: 0 }}
            />
          </Box>

          <Box sx={switchRowSx}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.smtpSSL}
                  onChange={handleToggle}
                  name="smtpSSL"
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Enable SSL
                  <FieldHint text="Enable when using port 465 (SSL/TLS). Leave off when using port 587 (STARTTLS), which upgrades the connection automatically." />
                </Box>
              }
              sx={{ m: 0 }}
            />
          </Box>
        </Paper>

        <Paper elevation={0} sx={settingsSectionSx}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
          >
            Email Settings
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
            Control sender identity, message format, and test delivery.
          </Typography>

          <TextField
            fullWidth
            label="From Email"
            name="fromEmail"
            value={settings.fromEmail}
            onChange={handleChange}
            placeholder="e.g. noreply@yourhoa.com"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FieldHint text="The sender address members will see in their inbox. Must be a valid address on your mail domain, or replies/bounces may be lost." />
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />

          <FormControl fullWidth sx={fieldSx}>
            <InputLabel>Email Format</InputLabel>
            <Select
              name="emailFormat"
              value={settings.emailFormat}
              label="Email Format"
              onChange={handleChange}
              endAdornment={
                <InputAdornment position="end" sx={{ mr: 3 }}>
                  <FieldHint text="PDF attaches the letter as a PDF file. HTML embeds the content directly in the email body. better for mobile reading." />
                </InputAdornment>
              }
            >
              <MenuItem value="PDF">PDF</MenuItem>
              <MenuItem value="HTML">HTML</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Test Recipient Email"
            name="testRecipient"
            value={settings.testRecipient}
            onChange={handleChange}
            placeholder="e.g. admin@yourhoa.com"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FieldHint text='The address that receives the test email when you click "Send Test Email". Use your own email to verify delivery and formatting.' />
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />

          <Divider sx={{ my: 2.5 }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Button
              variant="outlined"
              onClick={testSMTPConnection}
              disabled={loading}
              startIcon={loading ? null : <SendIcon />}
              sx={secondaryButtonSx}
            >
              {loading ? <CircularProgress size={20} /> : "Send Test Email"}
            </Button>

            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<SaveIcon />}
              sx={primaryButtonSx}
            >
              Save Settings
            </Button>
          </Box>

          {testResult && (
            <Box
              sx={{
                mt: 2,
                px: 1.5,
                py: 1.25,
                borderRadius: "12px",
                backgroundColor: testResult.includes("✅")
                  ? "rgba(46,125,50,0.08)"
                  : "rgba(211,47,47,0.08)",
                border: testResult.includes("✅")
                  ? "1px solid rgba(46,125,50,0.18)"
                  : "1px solid rgba(211,47,47,0.18)",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: testResult.includes("✅") ? "#2e7d32" : "#d32f2f",
                  fontWeight: 600,
                }}
              >
                {testResult}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );

  const renderTabContent = () => {
    switch (tabIndex) {
      case 0:
        return (
          <>
            <MemberDataSourceCard />
            <Company />
          </>
        );
      case 1:
        return <Notifications />;
      case 2:
        return renderEmailConfiguration();
      case 3:
        return <Uploads />;
      case 4:
        return <Statuses />;
      case 5:
        return <Roles />;
      case 6:
        return <PriorityLevels />;
      case 7:
        return <EmailTemplate />;
      case 8:
        return <IntegrationSettings />;
      case 9:
        return <HOADocuments />;
      case 10:
        return <NotificationLetter />;
      case 11:
        return <OwnerDashboardSettings />;
      case 12:
        return <AuditLogs />;
      case 20:
        return <EmailDeliveryLog />;
      case 13:
        return <MarinaSettings />;
      case 14:
        return <ModuleSettings />;
      case 15:
        return <SSOSettings />;
      case 16:
        return <AIChatSettings />;
      case 17:
        return <ApiKeysSettings />;
      case 100:
        return <WorkOrderManager />;
      case 101:
        return <ArchitecturalRequestManager />;
      case 102:
        return <ViolationstManager />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        minHeight: "100vh",
        p: { xs: 1.5, md: 2.5 },
        gap: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: isMobile ? "100%" : 280,
          borderRadius: "18px",
          border: "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          overflow: "hidden",
          alignSelf: "flex-start",
          backgroundColor: "rgba(255,255,255,0.96)",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 2,
            borderBottom: "1px solid #eef2f7",
            backgroundColor: "#fbfcfe",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
            Administration
          </Typography>
        </Box>

        <List sx={{ py: 1, px: 0.5 }}>
          {visibleGroups.map((group, gi) => {
            const isOpen    = openGroups[group.key] !== false;
            const hasActive = group.tabs.some(t => t.id === tabIndex);
            return (
              <React.Fragment key={group.key}>
                {/* Group divider. not before first group */}
                {gi > 0 && <Divider sx={{ mx: 1, my: 0.5, borderColor: "rgba(47,99,173,0.07)" }} />}

                {/* Group header. clickable to collapse */}
                <ListItemButton
                  onClick={() => toggleGroup(group.key)}
                  sx={{
                    mx: 0.5, mb: 0.25, borderRadius: "10px", py: 0.6,
                    "&:hover": { backgroundColor: "rgba(47,99,173,0.05)" },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28, color: hasActive ? "#2f63ad" : "#9aa5b4" }}>
                    {group.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={group.label}
                    primaryTypographyProps={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      color: hasActive ? "#2f63ad" : "#9aa5b4",
                    }}
                  />
                  {isOpen
                    ? <ExpandLess sx={{ fontSize: 16, color: "#b8c4d0" }} />
                    : <ExpandMore sx={{ fontSize: 16, color: "#b8c4d0" }} />}
                </ListItemButton>

                {/* Group items */}
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ mb: 0.5 }}>
                    {group.tabs.map(tab => (
                      <Tooltip
                        key={tab.id}
                        title={TAB_DESCRIPTIONS[tab.id] || ""}
                        placement="right"
                        arrow
                        disableHoverListener={isMobile}
                      >
                        <ListItemButton
                          selected={tabIndex === tab.id}
                          onClick={() => setTabIndex(tab.id)}
                          sx={{
                            pl: 3.5, mx: 0.5, my: 0.15, borderRadius: "10px", py: 0.6,
                            "&.Mui-selected": {
                              backgroundColor: "rgba(47,99,173,0.10)",
                              color: "#2f63ad",
                            },
                            "&.Mui-selected:hover": { backgroundColor: "rgba(47,99,173,0.16)" },
                            "&:hover": { backgroundColor: "rgba(47,99,173,0.04)" },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 34, color: tabIndex === tab.id ? "#2f63ad" : "#6b778c" }}>
                            {tab.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={tab.label}
                            primaryTypographyProps={{
                              fontSize: "0.85rem",
                              fontWeight: tabIndex === tab.id ? 700 : 500,
                              color: tabIndex === tab.id ? "#2f63ad" : "#374151",
                            }}
                          />
                        </ListItemButton>
                      </Tooltip>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          })}
        </List>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          p: { xs: 2, md: 3 },
          borderRadius: "18px",
          border: "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          minWidth: 0,
          backgroundColor: "rgba(255,255,255,0.96)",
        }}
      >
        <SectionHelp
          tabIndex={tabIndex}
          open={helpOpen}
          onToggle={() => setHelpOpen((v) => !v)}
        />
        {renderTabContent()}
      </Paper>

      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSaveSuccess(false)}>
          Settings saved successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={saveError}
        autoHideDuration={3000}
        onClose={() => setSaveError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setSaveError(false)}>
          Failed to save settings.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SystemAdministration;
